import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScopeContextStorage } from '../../shared/lib/scope-storage';
import { useUserProfile } from '../../features/auth/model/use-user-profile';
import type { ScopeContext } from '../../features/auth/model/types';
import { DebugLogger } from '../../shared/lib/debug';
import { exitToLogin } from '@/shared/lib/session-exit';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Copy } from 'lucide-react';
import { useLanguage } from '@/shared/hooks/use-language';
import { useToast } from '@/shared/hooks/use-toast';
import { ConsoleDashboardSkeleton } from '@/shared/components/ConsoleDashboardSkeleton';

function Console() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { toast } = useToast();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: t("integrations.copied") || "Copied!",
      description: `${label} ${t("integrations.copiedToClipboard") || "copied to clipboard"}`,
    });
  };
  const sessionId = location.state?.sessionId;
  const passedScopeContext = location.state?.scopeContext;
  
  const [scopeContext, setScopeContext] = useState<ScopeContext>(passedScopeContext || {});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUserProfile();
  const userName = typeof user?.name === 'string' && user.name ? user.name : null;

  useEffect(() => {
    DebugLogger.debug('Console component mounted', { 
      sessionId, 
      passedScopeContext,
      locationState: location.state 
    });
    
    // Try to get session ID from location state first, then from localStorage
    const currentSessionId = sessionId || ScopeContextStorage.getCurrentSessionId();
    
    if (!currentSessionId) {
      DebugLogger.error('No session ID found in location state or storage', { 
        locationState: location.state,
        pathname: location.pathname 
      });
      setError('No session ID found. Please log in again.');
      setIsLoading(false);
      return;
    }

    // Check if we have a scope context (either passed or stored)
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(currentSessionId);
    
    // ConsoleLayout owns the routing for an incomplete scope, for every /console/* route
    // rather than this one. Staying on the skeleton lets it navigate instead of rendering a
    // dashboard against a scope that is about to change.
    if (!contextToUse) return;
    if (ScopeContextStorage.needsAccountHolderSelection(contextToUse) && !contextToUse.account) return;
    if (ScopeContextStorage.needsAccountSelection(contextToUse)) return;

    DebugLogger.debug('Scope context is complete, initializing console', { 
      sessionId: currentSessionId, 
      contextToUse 
    });
    
    setScopeContext(contextToUse);
    setIsLoading(false);
  }, [sessionId, passedScopeContext, navigate, location.pathname]);

  if (isLoading) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <ConsoleDashboardSkeleton />
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <Card className="bg-destructive/10 border-destructive/20">
            <CardContent className="pt-6">
              <p className="text-destructive">
                <strong>{t("console.error")}</strong> {error}
              </p>
              <button
                onClick={() => exitToLogin()}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                {t("console.backToLogin")}
              </button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex-1">
              {userName ? (
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t("console.welcome").replace("{name}", userName)}
                </h1>
              ) : (
                <div
                  aria-hidden="true"
                  className="skeleton-shimmer h-9 w-72 rounded bg-muted mb-2"
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 animate-fadeIn">
            <Card className="bg-card border-border p-6">
              <CardHeader>
                <CardTitle>{t("console.scopeInfo")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {scopeContext.accountHolder && (
                    <div className="pb-4 border-b border-border">
                      <p className="text-sm text-muted-foreground mb-1">{t("console.accountHolder")}</p>
                      <p className="text-lg font-semibold text-foreground">
                        {scopeContext.accountHolder.name}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{t("console.taxId")}: {scopeContext.accountHolder.tax_id}</span>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(
                              scopeContext.accountHolder!.tax_id,
                              t("console.taxId"),
                            )
                          }
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={t("console.taxId")}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{t("console.id")}: {scopeContext.accountHolder.id}</span>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(
                              scopeContext.accountHolder!.id,
                              t("console.id"),
                            )
                          }
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={t("console.id")}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  {scopeContext.account && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{t("console.account")}</p>
                      <p className="text-lg font-semibold text-foreground">
                        {scopeContext.account.name}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span>{t("console.id")}: {scopeContext.account.id}</span>
                        <button
                          type="button"
                          onClick={() =>
                            copyToClipboard(
                              scopeContext.account!.id,
                              t("console.id"),
                            )
                          }
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={t("console.id")}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
  );
}

export default Console;
